import { Field } from "./DynamicForm";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useFormContext,
  UseFormSetValue,
} from "react-hook-form";
import DatePicker from "react-multi-date-picker";
import moment from "moment-jalaali";
import { useTranslation } from "react-i18next";
import { SlCalender } from "react-icons/sl";
import { formatLandline } from "services/helper";
import { IoMdClose } from "react-icons/io";
import { NumericFormat } from "react-number-format";
import PhoneInput from "components/input/PhoneInput";
import { countries } from "i18n";
import TextArea from "components/input/TextArea";
import InputField from "components/fields/InputField";
import Label from "components/label/Label";
import SmartSelect from "components/select/Select";
import Checkbox from "components/input/Checkbox";
import SmartUpload from "components/upload/FileUpload";
import { ScopeType } from "types/globalTypes";
import { useCalendarConfig } from "hooks/useCalendarConfig";
import UserFieldArray from "components/input/UserFieldArray";

type DynamicInputProps<T extends FieldValues> = {
  fields: Field[];
  control?: Control<T>;
  setValue?: UseFormSetValue<FieldValues>;
  readOnly?: boolean;
  filter?: boolean;
  role?: ScopeType;
};

const DynamicInput = <T extends FieldValues>({
  fields,
  control,
  setValue,
  readOnly,
  filter,
  role,
}: DynamicInputProps<T>) => {
  const { t } = useTranslation();
  const { calendar, locale, dir } = useCalendarConfig();

  const ctx = useFormContext?.() ?? null;
  const watch = ctx?.watch;
  const values = watch ? watch() : {};

  return (
    <>
      {fields.map(
        ({
          name,
          label,
          type,
          options,
          required,
          disabled,
          rows,
          onSearch,
          onRetry,
          onFieldChange,
          onBeforeChange,
          multiple,
          showWhen,
          permissions,
          picker,
          hidden,
          accept,
          gridCol,
          defaultSelectedOptions,
          defaultValue,
          other,
        }) => {
          if (permissions && role && !permissions.includes(role)) {
            return null;
          }

          if (showWhen && values) {
            const getNestedValue = (
              obj: { [x: string]: any },
              path: string
            ) => {
              if (!path) return undefined;

              if (path.includes(".")) {
                const parts = path.split(".");
                let result = obj;
                for (const part of parts) {
                  if (result === undefined || result === null) return undefined;
                  result = result[part];
                }
                return result;
              }
              return obj[path];
            };
            const target = getNestedValue(values, showWhen.field);
            let shouldShow = false;

            if (
              showWhen.equals === undefined &&
              showWhen.optionKey === undefined
            ) {
              shouldShow =
                target !== undefined && target !== null && target !== "";
            } else if (showWhen.optionKey !== undefined) {
              const mainField = fields.find((f) => f.name === showWhen.field);
              const mainOptions = mainField?.options || [];

              const selectedOption = mainOptions.find(
                (opt) => opt.value === target
              );

              shouldShow = selectedOption?.key === showWhen.optionKey;
            } else {
              shouldShow = target === showWhen.equals;
            }

            if (!shouldShow) {
              return null;
            }
          }

          if (hidden) return null;

          return (
            <div key={name} className={`col-span-${gridCol ?? 1} min-h-[90px]`}>
              {label && (
                <Label htmlFor={name}>
                  {label} {required && !filter && "*"}
                </Label>
              )}
              <Controller
                name={name as Path<T>}
                control={control}
                rules={{
                  required: filter
                    ? false
                    : required &&
                      (type === "date"
                        ? t("validation.dateRequired")
                        : t("validation.fieldRequired", { name: label })),
                  pattern:
                    name === "nationalCode"
                      ? {
                          value: /^\d+$/,
                          message: t("validation.onlyNumbers"),
                        }
                      : type === "phone"
                      ? {
                          value: /^0\d{2}\s\d{4}\s\d{4}$/,
                          message: t("validation.phone"),
                        }
                      : type === "mobile"
                      ? {
                          value: /^09\d{9}$/,
                          message: t("validation.mobile"),
                        }
                      : type === "email"
                      ? {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: t("validation.email"),
                        }
                      : undefined,
                  minLength:
                    type === "password"
                      ? {
                          value: 8,
                          message: t("validation.minLength", { min: 8 }),
                        }
                      : undefined,
                }}
                render={({ field, fieldState: { error } }) => {
                  switch (type) {
                    case "select":
                    case "search":
                      return (
                        <div className="w-full text-start">
                          <SmartSelect
                            mode={onSearch ? "dynamic" : "static"}
                            options={options}
                            value={field.value}
                            onChange={async (val) => {
                              if (onBeforeChange && name) {
                                const canChange = await onBeforeChange(
                                  val,
                                  values
                                );
                                if (!canChange) {
                                  return;
                                }
                              }

                              field.onChange(val);
                              if (onFieldChange) {
                                onFieldChange(val, setValue);
                              }
                            }}
                            multiple={multiple}
                            onSearch={onSearch}
                            onRetry={onRetry}
                            placeholder={t("actions.choose")}
                            error={error}
                            disabled={disabled}
                            defaultSelectedOptions={defaultSelectedOptions}
                            defaultValue={defaultValue}
                            other={other}
                          />

                          {error && (
                            <p className="mt-1 text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );

                    case "date":
                      return (
                        <div className="relative w-full" dir={dir}>
                          <DatePicker
                            value={field.value ? new Date(field.value) : ""}
                            readOnly={readOnly}
                            onChange={(date: any) => {
                              if (!date) {
                                field.onChange(null);
                                return;
                              }

                              const jsDate = date.toDate();
                              const momentDate = moment(jsDate);

                              if (picker === "year") {
                                field.onChange(momentDate.toISOString());
                              } else {
                                if (field.name === "startDate") {
                                  date = momentDate
                                    .startOf("day")
                                    .toISOString();
                                } else if (field.name === "endDate") {
                                  date = momentDate.endOf("day").toISOString();
                                } else {
                                  date = momentDate.toISOString();
                                }
                                field.onChange(date);
                              }
                            }}
                            calendar={calendar}
                            locale={locale}
                            onlyYearPicker={picker === "year"}
                            format={picker === "year" ? "YYYY" : "YYYY-MM-DD"}
                            calendarPosition="bottom-center"
                            fixMainPosition
                            portal
                            inputClass={`w-full h-11 rounded-lg border px-3 text-sm bg-white text-gray-800 border-gray-300 transition-all duration-200 dark:bg-gray-900 dark:text-white dark:border-white/20 hover:border-gray-400 dark:hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:ring-blue-400/30 dark:focus:border-blue-400 cursor-pointer`}
                            containerStyle={{
                              width: "100%",
                            }}
                          />

                          <span
                            className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 ltr:right-3 rtl:left-3`}
                          >
                            <SlCalender className="size-5" />
                          </span>

                          {field.value && !disabled && (
                            <div
                              className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 ltr:right-12 rtl:left-12`}
                            >
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  field.onChange(null);
                                }}
                                className="pointer-events-auto cursor-pointer text-gray-500 transition-colors hover:text-red-500 dark:hover:text-red-400"
                              >
                                <IoMdClose className="size-5" />
                              </span>
                            </div>
                          )}

                          {error && (
                            <p className="mt-1 text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );

                    case "time":
                      return (
                        <div>
                          <InputField
                            id={""}
                            label={""}
                            extra={""}
                            placeholder={""}
                            variant={""}
                            type="time"
                            readOnly={readOnly}
                            {...field}
                            state={error ? "error" : "default"}
                          />
                          {error && (
                            <p className="mt-1 text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );

                    case "price":
                      return (
                        <div>
                          <NumericFormat
                            readOnly={readOnly}
                            disabled={disabled}
                            thousandSeparator=","
                            suffix={
                              name === "exportAmount"
                                ? t("actions.dollar")
                                : t("actions.suffix")
                            }
                            getInputRef={field.ref}
                            value={field.value}
                            onValueChange={(values) => {
                              field.onChange(Number(values.value));
                            }}
                            className={`h-11 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 transition-all duration-200 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-white/20 dark:bg-gray-900 dark:text-white dark:hover:border-white/40 dark:focus:border-blue-400 dark:focus:ring-blue-400/30 ${
                              error
                                ? "border-red-500 focus:border-red-500 focus:ring-red-500/20 dark:border-red-400"
                                : ""
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                          />
                          {error && (
                            <p className="mt-1 text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );

                    case "mobile":
                      return (
                        <div>
                          <PhoneInput
                            {...field}
                            countries={countries}
                            readOnly={readOnly}
                            disabled={disabled}
                            placeholder="09123456789"
                            selectPosition="end"
                            onChange={(value) => field.onChange(value)}
                            error={error}
                          />
                          {error && (
                            <p className="mt-1 text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );

                    case "phone":
                      return (
                        <div>
                          <input
                            type="tel"
                            value={field.value || ""}
                            readOnly={readOnly}
                            disabled={disabled}
                            placeholder="028 3345 6677"
                            onChange={(e) => {
                              const formatted = formatLandline(e.target.value);
                              field.onChange(formatted);
                            }}
                            className={`h-11 w-full rounded-lg border border-gray-300 bg-white px-4 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-white/20 dark:bg-gray-900 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/30  ${
                              error
                                ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
                                : `border-gray-300focus:border-blue-500focus:ring-2focus:ring-blue-500/30dark:border-gray-700dark:focus:border-blue-400dark:focus:ring-blue-400/30`
                            }`}
                          />

                          {error && (
                            <p className="mt-1 text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );

                    case "textarea":
                      return (
                        <div>
                          <TextArea
                            {...field}
                            readOnly={readOnly}
                            disabled={disabled}
                            rows={rows || 3}
                            error={error ? true : false}
                          />
                          {error && (
                            <p className="text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );

                    case "checkbox":
                      return (
                        <div>
                          <Checkbox
                            checked={field.value}
                            onChange={field.onChange}
                            readOnly={readOnly}
                            disabled={disabled}
                            label={label}
                          />
                          {error && (
                            <p className="mt-1 block text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );

                    case "upload":
                      return (
                        <SmartUpload
                          field={field}
                          multiple={multiple}
                          disabled={disabled}
                          readOnly={disabled}
                          accept={accept}
                          error={error}
                        />
                      );

                    case "userArray":
                      return (
                        <UserFieldArray
                          control={control as any}
                          name={name}
                          error={error}
                        />
                      );

                    default:
                      return (
                        <div className="relative">
                          <InputField
                            id={""}
                            label={""}
                            extra={""}
                            placeholder={""}
                            variant={""}
                            {...field}
                            readOnly={readOnly}
                            type={type}
                            state={error ? "error" : "default"}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (type === "number") {
                                if (value === "") {
                                  field.onChange("");
                                  return;
                                }

                                const onlyPositive = value.replace(/-/g, "");
                                field.onChange(Number(onlyPositive));
                                return;
                              }

                              field.onChange(value);
                            }}
                            disabled={disabled}
                          />
                          {error && (
                            <p className="mt-1 text-xs text-red-500">
                              {error.message}
                            </p>
                          )}
                        </div>
                      );
                  }
                }}
              />
            </div>
          );
        }
      )}
    </>
  );
};

export default DynamicInput;
